import { onBeforeUnmount, onMounted, ref } from 'vue'

import { apiService } from '../services/api'
import { useAuthStore } from '../stores/auth-store'

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000

function isHttp429(e: unknown): boolean {
  return typeof (e as { status?: unknown })?.status === 'number' && (e as { status: number }).status === 429
}

function normalizeProviderKey(p: unknown): string {
  return String(p ?? '')
    .toLowerCase()
    .trim()
}

function uniqueProviders(list: Array<{ provider?: unknown }>): string[] {
  const seen = new Set<string>()
  for (const a of list) {
    const k = normalizeProviderKey(a.provider)
    if (!k || seen.has(k)) continue
    seen.add(k)
  }
  return Array.from(seen)
}

/**
 * While the user is in the app, request provider sync on an interval.
 * Backend enforces cooldown (often 2h) and may return 429; we treat that as a non-error.
 */
export function useDropshipAutoSync() {
  const authStore = useAuthStore()
  const timer = ref<number | null>(null)
  const inFlight = ref(false)

  const runOnce = async () => {
    if (!authStore.isAuthenticated) return
    if (document.visibilityState !== 'visible') return
    if (inFlight.value) return

    inFlight.value = true
    try {
      const res = await apiService.getDropshipAccounts()
      const providers = uniqueProviders(res.accounts)

      await Promise.all(
        providers.map(async (provider) => {
          try {
            await apiService.requestDropshipSync({ provider })
          } catch (e) {
            if (isHttp429(e)) return
            // Silent by design (no UX spam); errors will surface in the Integrations UI when needed.
          }
        }),
      )
    } catch (e) {
      if (isHttp429(e)) return
    } finally {
      inFlight.value = false
    }
  }

  onMounted(() => {
    void runOnce()
    timer.value = window.setInterval(() => {
      void runOnce()
    }, FIFTEEN_MINUTES_MS)
  })

  onBeforeUnmount(() => {
    if (timer.value != null) {
      window.clearInterval(timer.value)
      timer.value = null
    }
  })

  return { runOnce }
}
